import { ColumnDef } from '@tanstack/react-table';
import { SearchVariantItem } from '@texas/api/endpoints/search/searchVariantsTypes';
import {
  ArticleSearchProps,
  FilterOptions,
} from '@texas/components/searchArticles/types';
import { TFunction } from 'i18next';
import { PaSearchProps } from '../ProductApprovals/types';
import { ProductApprovalItem } from '@texas/api/endpoints/search/searchPaTypes';
import { EntityType } from '@texas/api/endpoints/trackerApi';
import { RefObject } from 'react';

export const defaultWidgetColor = 'texas.bg.800';
export const defaultWidgetLightColor = 'gray.100';

type State = 'hidden';

export enum WidgetType {
  Brands = 'brands',
  Articles = 'articles',
  ArticlesCount = 'articlesCount',
  ProductApprovalsCount = 'productApprovalsCount',
  TrackSamples = 'trackSamples',
  TrackInquiries = 'trackInquiries',
  MyActiveShipments = 'myActiveShipments',
  ProductApprovalLeadTime = 'productApprovalLeadTime',
  ProductApprovalRatio = 'productApprovalRatio',
  ArticleProgress = 'articleProgress',
  PaLeadTimeDistribution = 'paLeadTimeDistribution',
  SupplierCoverageHeatmap = 'supplierCoverageHeatmap',
  PaRejectionReasons = 'paRejectionReasons',
  Spacer = 'spacer',
  TrackOffers = 'trackOffers',
}

export type WidgetLayout = 'minified' | 'table';

export type SupplierCoverageHeatmapViewMode = 'suppliers' | 'articles';

export interface WidgetComponentSettings {
  settingDots?: State;
  editWidget?: State;
  editFilters?: State;
  notDraggable?: boolean;
}

export interface BaseWidgetOptions {
  readonly type: WidgetType;
  customWidgetTitle?: string;
  componentSettings?: WidgetComponentSettings;
}

export interface BrandsWidgetOptions extends BaseWidgetOptions {
  readonly type: WidgetType.Brands;
}

export interface MyActiveShipmentsWidgetOptions extends BaseWidgetOptions {
  readonly type: WidgetType.MyActiveShipments;
}

export interface SupplierCoverageHeatmapWidgetOptions
  extends BaseWidgetOptions {
  readonly type: WidgetType.SupplierCoverageHeatmap;
}

export interface SamplesOptions extends BaseWidgetOptions {
  readonly type: WidgetType.TrackSamples;
  trackerPointer: number | null;
  entityType: EntityType | null;
  localStorageKey: string | undefined;
  hideStopFollowing?: boolean;
  persistColumnVisibility?: boolean;
}

export interface InquiriesOptions extends BaseWidgetOptions {
  readonly type: WidgetType.TrackInquiries;
  trackerPointer: number | null;
  entityType: EntityType | null;
  localStorageKey: string | undefined;
}

export interface SearchArticleOptions extends BaseWidgetOptions {
  readonly type: WidgetType.Articles;
  defaultSearchPage: ArticleSearchProps;
  filterOptions: FilterOptions;
  pageSize: number;
  optOutDefaultOrdering: boolean;
  localStorageKey: string | undefined;
  columns: (t: TFunction) => ColumnDef<SearchVariantItem, any>[];
  persistColumnVisibility?: boolean;
}

export type SpacerTextAlignment = 'left' | 'center' | 'right';

export interface SpacerWidgetOptions extends BaseWidgetOptions {
  readonly type: WidgetType.Spacer;
  alignment: SpacerTextAlignment;
  fontSize: number;
  textColor: string | null;
}

export interface ArticlesCountOptions extends BaseWidgetOptions {
  readonly type: WidgetType.ArticlesCount;
  preConfiguredWidget?: Record<
    string,
    {
      defaultSearchPage: ArticleSearchProps;
      filterOptions: FilterOptions;
      optOutDefaultOrdering: boolean;
      localStorageKey: string | undefined;
      widgetTitle: string;
      componentSettings?: WidgetComponentSettings;
      columns: (t: TFunction) => ColumnDef<SearchVariantItem, any>[];
    }
  >;
  defaultSearchPage: ArticleSearchProps;
  filterOptions: FilterOptions;
  optOutDefaultOrdering: boolean;
  localStorageKey: string | undefined;
  columns: (t: TFunction) => ColumnDef<SearchVariantItem, any>[];
}

export interface PaCountOptions extends BaseWidgetOptions {
  readonly type: WidgetType.ProductApprovalsCount;
  preConfiguredWidget?: Record<
    string,
    {
      defaultSearchPage: PaSearchProps;
      filterOptions: FilterOptions;
      optOutDefaultOrdering: boolean;
      localStorageKey: string | undefined;
      widgetTitle: string;
      componentSettings?: WidgetComponentSettings;
      columns: (t: TFunction) => ColumnDef<ProductApprovalItem, any>[];
    }
  >;
  defaultSearchPage: PaSearchProps;
  filterOptions: FilterOptions;
  optOutDefaultOrdering: boolean;
  localStorageKey: string | undefined;
  columns: (
    t: TFunction,
    containerRef: RefObject<HTMLDivElement>,
  ) => ColumnDef<ProductApprovalItem, any>[];
}

export interface ProductApprovalLeadTimeOptions extends BaseWidgetOptions {
  readonly type: WidgetType.ProductApprovalLeadTime;
  customerId?: number;
  branchIds?: number[];
}

export interface ProductApprovalRatioOptions extends BaseWidgetOptions {
  readonly type: WidgetType.ProductApprovalRatio;
  customerId?: number;
  branchIds?: number[];
}

export interface ArticleProgressOptions extends BaseWidgetOptions {
  readonly type: WidgetType.ArticleProgress;
  customerId?: number;
  branchIds?: number[];
}

export interface PaLeadTimeDistributionOptions extends BaseWidgetOptions {
  readonly type: WidgetType.PaLeadTimeDistribution;
  customerIds?: number[];
  branchIds?: number[];
}

export interface PaRejectionReasonsOptions extends BaseWidgetOptions {
  readonly type: WidgetType.PaRejectionReasons;
  customerId?: number;
  branchIds?: number[];
  supplierId?: number;
}

export interface TrackOffersWidgetOptions extends BaseWidgetOptions {
  readonly type: WidgetType.TrackOffers;
}

export type WidgetOptions =
  | BrandsWidgetOptions
  | SearchArticleOptions
  | ArticlesCountOptions
  | PaCountOptions
  | SamplesOptions
  | InquiriesOptions
  | MyActiveShipmentsWidgetOptions
  | ProductApprovalLeadTimeOptions
  | ProductApprovalRatioOptions
  | ArticleProgressOptions
  | PaLeadTimeDistributionOptions
  | SupplierCoverageHeatmapWidgetOptions
  | PaRejectionReasonsOptions
  | SpacerWidgetOptions
  | TrackOffersWidgetOptions;
